import React, {useEffect, useMemo, useRef, useState} from 'react';
import {getMarketInfo} from '../contexts/market';
import {Form, useFormMutator, useFormState} from '../components/form';
import {Button, Icon, SubmitButton} from '../components/controls';
import {Checkbox, CreditCardField, DoterraSelect, Field, Hidden, TextField} from '../components/fields';
import {text} from '../utils/i18n';
import {doterraSelectMonthOptionData, doterraSelectYearOptionData, monthOptionData, yearOptionData} from "../utils/payments";
import {defaultPostalCodeFormatter} from "../utils/common";
import {PaymentAddressPanel} from "./payment-address-panel";
import {CreditCardType, McAfeeLogo} from "../components/payments";
import {usePaymentsMutator, usePaymentsState} from "../contexts/payments";
import {useModalsMutator, useSiteState} from "../contexts/page";
import {getFormatter, getSupportedCCTypes, getSupportedCountryNames, getSupportedRegions} from "../data";
import {usePaymentViewMutator, usePaymentViewState} from "./payment-view";
import {ConfirmationModal, SuccessModal} from '../components/modals';
import {generateSchema, prop} from '../utils/schema';
import {post} from '../utils/rmi';
import {useCustomerState} from "../contexts/customer";
import {useDates} from '../utils/dates';
import {Tooltip} from 'react-tooltip';
import ReCAPTCHA from "react-google-recaptcha";
import {flushSync} from "react-dom";

const buttonCentering = {
  margin: '20px auto'
};
const iconHeaderStyling = {
  marginTop: '15px'
};
const cancelStyling = {
  fontWeight: '400',
  lineHeight: '0.95'
};
const citMessageStyling = {
  fontSize: '12px',
  marginBottom: '8px'
};

export const CreditCardForm = (props) => {
  const {markets, mode, cardMessage, recaptchaKey, recaptchaRef, cartShippingAddress} = props;

  // STATE //
  const {site, language} = useSiteState();
  const {hasActiveLrp} = useCustomerState();
  let {editTarget: payment, uniqueAddressInfo: addresses, methods, context, cartPaymentId, isRetail = false, isSubscriptionsPage} = usePaymentsState();
  const [market, setFormMarketState] = useState(payment && payment.billingAddress ? payment.billingAddress.region.countryIso : site);
  const {viewOptions} = usePaymentViewState();
  const {showGlobalModal, hideGlobalModal} = useModalsMutator();
  payment = mode === 'addCard' ? null : payment;


  // MUTATORS //
  const {setFieldValue, setFieldValues, setInitialValues} = useFormMutator();
  const {isSubmitting, initialized, values} = useFormState();
  const [isCvvEmpty, setCvvEmptyVal] = useState(values.cvv.length < 3 && mode !== "edit" ? true : false);
  const {showPaymentList, showPaymentSelect} = usePaymentViewMutator();
  const {removePayment} = usePaymentsMutator();
  let [cardNumberPassed, setCardNumberPassed] = useState(false);

  const getCurrentFieldValues = () => {
    return values;
  };

  // MARKET SUPPORT //
  const supportedCountries = getSupportedCountryNames(language, markets).map((country) => {
    return {value:country.value, label:country.name};
  });
  let marketAddresses = addresses.filter(address => address.country.isocode === market);
  const {getDateInfo} = useDates();

  let showBillingAddress;
  if(marketAddresses && marketAddresses.length > 0) {
    showBillingAddress = marketAddresses[0];
  }

  if((mode === 'addCard') && cartShippingAddress) {
    showBillingAddress = cartShippingAddress;
  }
  else if((mode === 'edit') && payment) {
    showBillingAddress = payment.billingAddress;
  }

  useEffect(() => {
    if(initialized) {
      return;
    }

    const formValues = {
      id: payment ? payment.id : '',
      cvv: '',
      ccNumber: payment ? payment.number : '',
      nameOnCard: payment ? payment.holderName : '',
      expMonth: payment ? payment.expiryMonth : getDateInfo().textMonth,
      expYear: payment ? payment.expiryYear : yearOptionData[0].props.value,
      billingLine1: payment && payment.billingAddress ? payment.billingAddress.line1 : showBillingAddress ? showBillingAddress.line1 : '',
      billingLine2: payment && payment.billingAddress ? payment.billingAddress.line2 : showBillingAddress ? showBillingAddress.line2 : '',
      billingCity: payment && payment.billingAddress ? payment.billingAddress.town : showBillingAddress ? showBillingAddress.town : '',
      billingState: payment && payment.billingAddress ? payment.billingAddress.region.isocodeShort : showBillingAddress ? showBillingAddress.region.isocodeShort : '',
      billingZip: payment && payment.billingAddress ? payment.billingAddress.postalCode : showBillingAddress ? showBillingAddress.postalCode : '',
      market: payment && payment.billingAddress ? payment.billingAddress.region.countryIso : market,
      cardType: payment ? payment.type : '',
      makeDefault: payment ? payment.defaultPayment : !viewOptions.makeDefault,
      recaptchaResponse: ''
    };
    setInitialValues(formValues);
  });

  // HANDLERS //
  const handleDelete = () => {
    const routeToShopHome = () => {
      let redirectUrl = contextPath + "/shop";
      let isSubscriptionEnabled = $('#isSubscriptionEnabled').val() === 'true';
      if(isSubscriptionEnabled) {
        redirectUrl = contextPath + '/subscriptions';
      }
      window.location.href = redirectUrl;
    };

    const deleteConfirmed = () => {
      removePayment(payment.id).then(result => {
        if(result) {
          showPaymentList();
        }
      });
    };

    const deletePaymentModal = () => {
      const modalName = 'confirm-payment-delete';
      showGlobalModal(modalName, <ConfirmationModal
          type='warning'
          text={text('form.paymentMethod.confirmDelete')}
          rejectText={text('text.button.label.no')}
          rejectClassNames='info'
          acceptText={text('text.button.label.yes')}
          accept={deleteConfirmed}
          close={() => hideGlobalModal(modalName)}
        />
      );
    };

    if (isRetail) {
      deletePaymentModal();
    }
    else {
      post("/my-account/checkForLRPTemplate/Payment/" + payment.id, null, (isLRPPayment) => {
        if(isLRPPayment) {
          const modalName = 'lrp-payment-check';
          showGlobalModal(modalName, <ConfirmationModal
              type='warning'
              text={text('form.paymentMethod.confirmLRP')}
              rejectText={text('form.paymentMethod.returnToPayments')}
              rejectClassNames='info'
              acceptText={text('text.button.label.goToLRP')}
              accept={routeToShopHome}
              close={() => hideGlobalModal(modalName)}
            />
          );
        }
        else {
          deletePaymentModal();
        }
      }, null, null);
    }
  };

  const handleMarketChange = (value, fields) => {
    setFormMarketState(value);
    fields.market = value;
    if (fields.market !== value) {
      marketAddresses = addresses.filter(address => address.country.isocode === value);
      if(marketAddresses.length > 0) {
        fields.billingLine1 = marketAddresses[0].line1;
        fields.billingLine2 = marketAddresses[0].line2;
        fields.billingCity = marketAddresses[0].town;
        fields.billingState = marketAddresses[0].region.isocodeShort;
        fields.billingZip = marketAddresses[0].postalCode;
      }
      else {
        // We need to empty out the billing address because we dont want a
        // previous market address to be there still
        fields.billingLine1 = '';
        fields.billingLine2 = '';
        fields.billingCity = '';
        fields.billingState = '';
        fields.billingZip = '';
      }
      setFieldValues(fields);
    }
  };

  const handleCvvChange = (value, fields) => {
    flushSync(() => {
      setCvvEmptyVal(true);
    });

    let cvvCheckLength = 3;
    if (fields.cardType === 'amex') {
      cvvCheckLength = 4;
    }

    if(fields.cvv.length === cvvCheckLength) {
      flushSync(() => {
        setCvvEmptyVal(false);
      });
    }
  };

  let cvvMask = 'NNN';
  if(values && values.cardType && values.cardType === 'amex') {
    cvvMask = 'NNNN';
  }

  const cvvTemplate = {
    mask: cvvMask,
    upperCase: false
  };

  const HandleCardNumberPassed = (val) => {
    queueMicrotask(() => {
      setCardNumberPassed(val);
    });
  };

  const marketRegion = getMarketInfo(market);

  let isPostalValid = '';
  let regexExp = new RegExp(marketRegion[1].postalCode.pattern);
  isPostalValid = regexExp.test(values.billingZip);

  // EDIT VS ADD (may need to have edit and add abstracted out to their own components)//
  const ManageTitle = () => {
    return mode === "edit" ? <>{text("form.paymentManagement.editPaymentHeader")}</> : <>{text("form.paymentManagement.addCardHeader")}</>;
  };

  const DeletePayment = () => {
    return mode === "edit" && payment && (!payment.defaultPayment || (payment.defaultPayment && !hasActiveLrp)) && (!cartPaymentId || payment.id !== cartPaymentId)
      ? <div className={"btn btn-link"} onClick={handleDelete} style={cancelStyling}>{text("form.paymentManagement.deletePayment")}</div> : null;
  };

  let tooltipMessage = '';
  if(values && values.cardType) {
    tooltipMessage = values.cardType === 'amex' ? text("form.paymentManagement.cvv.tooltip.amex") : text("form.paymentManagement.cvv.tooltip.others");
  }
  const cvvTooltipId = "creditCardCVVToolTip";
  const cvvField = mode === "edit" ? null :
    <Field component={TextField}
           id="cvv"
           name="cvv"
           onKeyUp={e => handleCvvChange(e.target.value, values)}
           labelKey={text("form.paymentManagement.cvv.label")}
           classes="form-control"
           placeholder="000"
           template={cvvTemplate} tooltipMessage={tooltipMessage} tooltipId={cvvTooltipId} isIconEnabled={true} validateField={isCvvEmpty}/>;

  const supportedCardTypesArray = getSupportedCCTypes(language).filter(ccType => (ccType.value === market));
  const supportedCardTypes = (supportedCardTypesArray.length > 0 && supportedCardTypesArray[0].name);

  return (
    <div className="row">
      <div className={`col-xs-12${viewOptions.fullWidth ? '' : ' col-md-6'}`}>
        <div className='manage-credit-card panel panel-product panel-default doterra-payment-item'>
          <div className='list-group-item'>
            <div className='payment-header'>
              <div className="payment-title">
                <Icon name='payment-generic' size='medium' cssStyles={iconHeaderStyling}/>
                <h5><ManageTitle/></h5>
              </div>
              <div className="payment-back">
                {(context !== 'enrollment' || methods.length > 1) &&
                  <Button id={"hidePayment"} disabled={false}
                          text={text("text.button.label.back")}
                          classes={"btn btn-link"}
                          ariaLabel={text("text.button.label.back.payment.method")}
                          onClick={mode === 'edit' ? showPaymentList : showPaymentSelect} cssStyles={cancelStyling}/>
                }
              </div>
            </div>
            <Hidden name='cardType' className="form-control"/>
            <div className="row">
              <div className="form-group col-xs-12">
                <Field component={DoterraSelect} id='market' name='market' labelText={text("form.paymentManagement.market.label")} labelClasses='dropdown-label'
                       onChange={e => handleMarketChange(e.target.value, values)} options={supportedCountries} />
              </div>
            </div>
            <div>
              <CreditCardType ccType={payment ? payment.type : null} supportedCardTypes={supportedCardTypes}/>
            </div>
            <div className="row">
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={CreditCardField} id='ccNumber' name='ccNumber' mode={mode} autoCompleteValue='cc-csc'
                       labelKey={"text.paymentManagement.cardType.otherCard"} validateFieldFunc={HandleCardNumberPassed}/>
              </div>
              <div className="form-group col-xs-12 col-sm-6">
                {cvvField}
              </div>
            </div>
            <div className="row">
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={DoterraSelect} id="expMonth" name="expMonth" labelClasses='dropdown-label' labelText={text("form.paymentManagement.expMonth.label")}
                       addClasses="expMonth" disabled={false} options={doterraSelectMonthOptionData} />

              </div>
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={DoterraSelect} id="expYear" name="expYear" labelClasses='dropdown-label' labelText={text("form.paymentManagement.expYear.label")}
                       addClasses="expYear" disabled={false} options={doterraSelectYearOptionData} />

              </div>
            </div>
            <div className="form-group">
              <Field component={TextField} id="nameOnCard" name="nameOnCard" labelKey={"form.paymentManagement.nameOnCard.label"} classes="form-control"
                     placeholder={"Full name as it appears"} required={true} maxLength="35"/>
            </div>

            <div id="billing-address-section">
              <PaymentAddressPanel mode={mode} payment={payment ? payment : null} addresses={marketAddresses} market={market} regions={getMarketInfo(market)}
                                   setForm={setFieldValue} useSavedAddress={viewOptions.useSavedAddress} cartShippingAddress={showBillingAddress}
                                   getCurrentFieldValues={getCurrentFieldValues}/>
            </div>
            {viewOptions.makeDefault && !isSubscriptionsPage &&
              <Field component={Checkbox} id="makeDefault" name="makeDefault" labelKey={"form.paymentManagement.makeDefault"} classes="form-control"
                     labelClasses="shipping-label"/>
            }
            {viewOptions.showAddButton &&
              <div className="text-center">
                <SubmitButton id={"paymentAddSubmit"} disabled={isSubmitting || ((mode !== "edit") && (isCvvEmpty || !cardNumberPassed)) || !isPostalValid}
                              text={mode === "edit" ? text("form.paymentManagement.savePayment") : text("form.paymentManagement.addPayment")} classes={"btn btn-primary"}
                              cssStyles={buttonCentering}/>
              </div>
            }
            <div className="text-center">
              <DeletePayment/>
            </div>
            <div className="text-left">
              <div style={{fontSize: '12px'}}>
                {text('doterra.recaptcha.sitemessage')} <a href="https://policies.google.com/privacy">{text('doterra.recaptcha.privacypolicymessage')}</a> {text(
                'doterra.recaptcha.andmessage')} <a href="https://policies.google.com/terms">{text('doterra.recaptcha.termservicemessage')}</a> {text(
                'doterra.recaptcha.applymessage')}
              </div>
            </div>
            <div style={citMessageStyling} dangerouslySetInnerHTML={{__html: cardMessage}}/>
            <div className="text-center">
              <McAfeeLogo/>
            </div>
            <div id="recaptchacontainer" className="col-xs-12 col-lg-6 grecaptcha-badge">
              <ReCAPTCHA ref={recaptchaRef} size='invisible' badge='inline' className="pull-right" sitekey={recaptchaKey} hl={language}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CrmCreditCardForm = (props) => {
  let {markets, mode, cardMessage, modelName, recaptchaKey, recaptchaRef} = props;

  // STATE //
  const {contactAddress} = useCustomerState();
  const {site, language} = useSiteState();
  const {hideModal, setPaymentSave, setAcceptPaymentTerms} = usePaymentsMutator();
  let {acceptPaymentTerms: acceptPaymentTerms, editTarget: payment, uniqueAddressInfo: addresses} = usePaymentsState();

  const [market, setFormMarketState] = useState(payment && payment.billingAddress ? payment.billingAddress.region.countryIso : site);
  const {viewOptions} = usePaymentViewState();

  // MUTATORS //
  const {setFieldValues, setInitialValues} = useFormMutator();
  const {isSubmitting, initialized, values} = useFormState();
  const marketRegion = getMarketInfo(market);
  let [crmCardNumberPassed, setCrmCardNumberPassed] = useState(false);
  let crmTokenizationEnabled = $('#crmTokenizationEnabled').val() === 'true';

  let isPostalValid = '';
  let regexExp = new RegExp(marketRegion[1].postalCode.pattern);
  isPostalValid = regexExp.test(values.billingZip);

  if(mode === 'crmedit') {
    isPostalValid = true;
  }

  const [postalValidity, setPostalValidity] = useState(isPostalValid);
  if(modelName !== 'crmAddLRPNewCard' && modelName !== 'crmSubscriptionAddNewCard') {
    cardMessage = null;
  }

  if(!cardMessage) {
    acceptPaymentTerms = true;
  }

  // MARKET SUPPORT //
  const doterraSelectSupportedCountries = getSupportedCountryNames(language, markets).map((country) => {
    return {label: country.name, value: country.value};
  });
  let marketAddresses = addresses.filter(address => address.country.isocode === market);

  // State or Province Support //
  const supportedRegionsArray = getSupportedRegions(language).filter(region => (region.value === market));
  const doterraSelectSupportedRegions = (supportedRegionsArray.length > 0 && supportedRegionsArray[0].name).map((region) => {
    return {label: region.name, value: region.isocodeShort};
  });

  function crmCardType(cardType) {

    switch(cardType) {
      case 'master': {
        return 'Mastercard';
      }
      case 'visa': {
        return 'Visa';
      }
      case 'amex': {
        return 'AMEX';
      }
      case 'jcb': {
        return 'JCB';
      }
      case 'discover': {
        return 'Discover';
      }
      case 'diners': {
        return 'Diner';
      }
      default: {
        return cardType;
      }
    }
  }

  // Card Type //
  const supportedCardTypesArray = getSupportedCCTypes(language).filter(ccType => (ccType.value === market));

  const doterraSelectSupportedCardTypes = (supportedCardTypesArray.length > 0 && supportedCardTypesArray[0].name).map((ccType) => {
    const ccTypeValue = crmCardType(ccType.key);
    return {label: ccType.name, value: ccTypeValue};
  });

  useEffect(() => {
    $('.doterra-select').selectpicker('refresh');
  });

  const cancelEditPaymentClicked = () => {
    hideModal(modelName);
  };

  const paymentValues = (showPayment, isInit) => {
    const showPaymentValues = {
      id: showPayment ? showPayment.id : '',
      cvv: showPayment && showPayment.cvv ? showPayment.cvv : '',
      ccNumber: showPayment ? showPayment.number : '',
      nameOnCard: showPayment ? showPayment.holderName : '',
      expMonth: showPayment ? showPayment.expiryMonth : monthOptionData[0].value,
      expYear: showPayment ? showPayment.expiryYear : yearOptionData[0].value,
      billingLine1: showPayment && showPayment.billingAddress ? showPayment.billingAddress.line1 : '',
      billingLine2: showPayment && showPayment.billingAddress ? showPayment.billingAddress.line2 : '',
      billingCity: showPayment && showPayment.billingAddress ? showPayment.billingAddress.town : '',
      billingState: showPayment && showPayment.billingAddress ? showPayment.billingAddress.region.isocodeShort : '',
      billingZip: showPayment && showPayment.billingAddress ? showPayment.billingAddress.postalCode : '',
      market: showPayment && showPayment.billingAddress ? showPayment.billingAddress.region.countryIso : market,
      cardType: showPayment ? crmCardType(showPayment.type) : '',
      makeDefault: showPayment ? showPayment.defaultPayment : false,
      recaptchaResponse: ''
    };

    if(isInit) {
      setInitialValues(showPaymentValues);
    }
    else {
      setFieldValues(showPaymentValues);
    }
  };

  const acceptPaymentTermsHandler = (e) => {
    if (e.target.checked) {
      setAcceptPaymentTerms(true);
    }
    else {
      setAcceptPaymentTerms(false);
    }
  };

  const HandleCRMCardNumberPassed = (val) => {
    if (crmTokenizationEnabled) {
      setCrmCardNumberPassed(val);
    }
    else {
      // purposely blank
      return null;
    }
  };

  const useDefaultValues = (e) => {
    let crmShowAddress = null;
    if(e.target.checked) {
      crmShowAddress = contactAddress;
    }
    else if(mode === 'crmedit') {
      crmShowAddress = marketAddresses && marketAddresses.length > 0 && marketAddresses[0];
      if(payment && payment.billingAddress) {
        crmShowAddress = payment.billingAddress;
      }
    }

    let isMarketNotChanged = true;
    if(crmShowAddress && crmShowAddress.country) {
      if(crmShowAddress.country.isocode !== values.market) {
        isMarketNotChanged = false;
      }

      flushSync(() => {
        setFormMarketState(crmShowAddress.country.isocode);
      });
    }

    const crmShowPayment = {
      id: values ? values.id : '',
      number: values ? values.ccNumber : '',
      holderName: values ? values.nameOnCard : '',
      expiryMonth: values ? values.expMonth : '',
      expiryYear: values ? values.expYear : '',
      type: values && isMarketNotChanged ? values.cardType : '',
      cvv: values && values.cvv ? values.cvv : '',
      defaultPayment: values ? values.makeDefault : '',
      billingAddress: crmShowAddress
    };
    setPostalValidity(true);
    paymentValues(crmShowPayment, false);
  };

  useEffect(() => {
    if(initialized) {
      return;
    }

    let showPayment = null;
    let showAddress = null;
    if(mode !== 'crmaddCard') {
      showAddress = marketAddresses && marketAddresses.length > 0 && marketAddresses[0];
      if(payment) {
        showPayment = payment;
        if(payment.billingAddress) {
          showAddress = payment.billingAddress;
        }
      }

      if(showPayment) {
        showPayment.billingAddress = showAddress;
      }
    }

    paymentValues(showPayment, true);
  });

  const crmCVVTemplate = {
    mask: 'NNNN',
    upperCase: false
  };

  const formatter = useMemo(() => getFormatter(market, 'postalCode') || defaultPostalCodeFormatter, [market]);

  const handleKeyUp = (element) => {
    let postcodeRegexExp = new RegExp(marketRegion[1].postalCode.pattern);
    let isValid = postcodeRegexExp.test(element.value);
    if(!isValid) {
      setPostalValidity(false);
    }
    else {
      setPostalValidity(true);
    }
  };
  const handleCRMMarketChange = (value, fields) => {
    setFormMarketState(value);
    fields.market = value;

    fields.billingLine1 = '';
    fields.billingLine2 = '';
    fields.billingCity = '';
    fields.billingState = '';
    fields.billingZip = '';

    if(mode !== "crmedit") {
      fields.cardType = '';
    }

    const useProfileAddressCheckbox = document.getElementById('useProfileAddress');
    if(useProfileAddressCheckbox) {
      useProfileAddressCheckbox.checked = false;
    }
    setFieldValues(fields);
    setPostalValidity(false);
  };

  const useAndSave = () => {
    setPaymentSave(true);
  };

  const useAndNotSave = () => {
    setPaymentSave(false);
  };

  const [copyTerms, setCopyTerms] = useState({
                                               displayText: "Copy payment terms agreement",
                                               copyTerms: "terms agreement"
                                             });

  // EDIT VS ADD (may need to have edit and add abstracted out to their own components)//
  const ManageTitle = () => {
    return mode === "crmedit" ? <>{text("form.enrollment.payment.billing.editAddress.label")} {payment ? crmCardType(payment.type) : ''}</> :
      modelName === 'crmAddNewCard' ? <>{text("form.paymentManagement.addCardHeader")}</> : <>{text("crm.order.payment.form.card.add.text")}</>;
  };

  const inputEl = useRef(null);
  const textAreaEl = useRef(null);

  const onCopyAgreementHandler = () => {
    Tooltip.hide(inputEl.current);
    flushSync(() => {
      setCopyTerms({
                     displayText: 'Agreement terms copied',
                     copyTerms: `${cardMessage}`
                   });
    });
    textAreaEl.current.select();
    document.execCommand("copy");

    setTimeout(() => {
      Tooltip.show(inputEl.current);
    }, 1);

  };

  return (
    <div className="row">
      <div className={`col-md-12${viewOptions.fullWidth ? '' : ' col-md-6'}`}>
        <div className='manage-credit-card panel panel-product panel-default'>
          <div className='list-group-item'>
            <div className='row'>
              <div className='col-xs-8'>
                <h5><ManageTitle/></h5>
              </div>
            </div>
            {/* Name and Country */}
            <div className="row">
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={TextField} id="crmNameOnCard" name="nameOnCard" labelKey={"form.paymentManagement.nameOnCard.label"} classes="form-control"
                       placeholder={"Full name as it appears"} required={true} maxLength="35"/>
              </div>
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={DoterraSelect} id='crmCountry' name='market' labelText={text("address.country")} labelClasses='dropdown-label' onChange={e => {
                  handleCRMMarketChange(e.target.value, values);
                }} options={doterraSelectSupportedCountries} isSearchable={false}/>
              </div>
            </div>
            {/* Credit Card Number and Billing Address */}
            <div className="row">
              <div className="form-group col-xs-12 col-sm-6">
                {crmTokenizationEnabled && <Field component={CreditCardField} id='ccNumber' name='ccNumber' mode={mode} autoCompleteValue='cc-csc'
                                                  labelKey={"form.paymentManagement.ccNumber.label"} disabled={mode === 'crmedit' ? true : false}
                                                  validateFieldFunc={HandleCRMCardNumberPassed}/>}
                {!crmTokenizationEnabled &&
                  <Field component={TextField} id='crmCCNumber' name='ccNumber' mode={mode} labelKey={"form.paymentManagement.ccNumber.label"} classes="form-control"
                         required={true} maxLength="25" readOnly={mode === 'crmedit' ? true : false}/>}
              </div>
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={TextField} id="crmBillingAddress" name="billingLine1" labelKey={"form.paymentManagement.billingLine1.label"} classes="form-control"
                       placeholder={"Billing Address as it appears"} required={true} maxLength="35"/>
              </div>
            </div>
            {/* Card Type and Billing Address 2 */}
            <div className="row">
              <div className="form-group col-xs-12 col-sm-6">
                {mode === 'crmedit' && <Field component={CreditCardField} id='crmCardType' name='cardType' labelKey={"form.paymentManagement.cardTypes.label"}
                                              validateFieldFunc={HandleCRMCardNumberPassed}/>}
                {mode !== 'crmedit' &&
                  (<Field component={DoterraSelect} id='crmCardType' name='cardType' labelText={text("form.paymentManagement.cardTypes.label")}
                          labelClasses='dropdown-label' options={doterraSelectSupportedCardTypes} isSearchable={false}/>)}
              </div>
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={TextField} id="crmBillingAddress2" name="billingLine2" labelKey={"form.paymentManagement.billingLine2.label"} classes="form-control"
                       placeholder={"Billing Address 2 as it appears"} maxLength="35"/>
              </div>
            </div>
            {/* Exp Month and City*/}
            <div className="row">
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={DoterraSelect} id="crmExpMonth" name="expMonth" labelClasses='dropdown-label' labelText={text("form.paymentManagement.expMonth.label")}
                       addClasses="expMonth" options={doterraSelectMonthOptionData} isSearchable={false}/>
              </div>
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={TextField} id="crmCity" name="billingCity" labelKey={`form.paymentManagement.billingCity.label.${market}`} classes="form-control"
                       placeholder={"City as it appears"} required={true} maxLength="35"/>
              </div>
            </div>
            {/* Exp Year and State*/}
            <div className="row">
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={DoterraSelect} id="crmExpYear" name="expYear" labelClasses='dropdown-label' labelText={text("form.paymentManagement.expYear.label")}
                       addClasses="expYear" options={doterraSelectYearOptionData} isSearchable={false}/>
              </div>
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={DoterraSelect} id="crmState" name="billingState" labelClasses='dropdown-label'
                       labelText={text(`form.paymentManagement.billingState.label.${market}`)} addClasses="expState" options={doterraSelectSupportedRegions}
                       isSearchable={false}/>
              </div>
            </div>
            <div className="row">
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={TextField} id="crmCVV" name="cvv" labelKey={"form.paymentManagement.cvv.label"} classes="form-control" placeholder={"CVV as it appears"}
                       template={crmCVVTemplate} required={true}/>
              </div>
              <div className="form-group col-xs-12 col-sm-6">
                <Field component={TextField} id="crmPostCode" name="billingZip" labelKey={"form.paymentManagement.billingZip.label"} classes="form-control"
                       placeholder={"Post Code as it appears"} required={true} maxLength="35" template={formatter} useRestricted={true}
                       onKeyUp={e => handleKeyUp(e.target)} valid={postalValidity}/>
              </div>
            </div>

            <Field component={Checkbox} id="useProfileAddress" name="useProfileAddress" labelKey={"crm.payment.use.profile.address"} classes="form-control"
                   labelClasses="shipping-label" onChange={useDefaultValues}/>

            <textarea data-testid="copiedText"
                      ref={textAreaEl} defaultValue={cardMessage} style={{opacity: 0, position: 'absolute'}}/>
            <div className="row">
              <div className="col-xs-12 col-sm-11">
                <div style={citMessageStyling} dangerouslySetInnerHTML={{__html: cardMessage}}/>
              </div>
              <div className="col-xs-12 col-sm-1">
                {cardMessage &&
                  <span>
                      <i data-testid="copyToClipBoard" ref={inputEl} onClick={onCopyAgreementHandler} className="fa fa-copy"
                        data-tooltip-content={copyTerms.displayText}
                        data-tooltip-id="termsCopy" onMouseLeave={() => {
                          setCopyTerms({...copyTerms, displayText: "Copy payment terms agreement"});
                          Tooltip.hide();
                        }}
                      />
                      <Tooltip id="termsCopy" variant="light"/>
                  </span>}
              </div>
            </div>

            <div className="row">
              <div className="col-xs-12 col-sm-12">
                {cardMessage &&
                  <Field component={Checkbox} id="acceptPaymentTerms" name="acceptPaymentTerms" labelKey={"crm.payment.agree.payment.terms"} classes="form-control"
                         labelClasses="shipping-label" onChange={acceptPaymentTermsHandler}/>}
              </div>
            </div>
            <div className="text-right">
              {(mode === 'crmedit') && <SubmitButton id="crmPaymentEditSubmit" disabled={isSubmitting || !postalValidity}
                                                     text={mode === "crmedit" ? text("form.generic.button.save") : text("form.paymentManagement.addPayment")}
                                                     classes={"btn btn-primary"} cssStyles={buttonCentering}/>}
              {(mode === 'crmaddCard' && modelName === 'crmAddNewCard') && <>
                <SubmitButton id="crmPaymentUseSaveSubmit"
                              text={text("crm.text.payment.use.and.save")}
                              disabled={isSubmitting || !postalValidity || !crmCardNumberPassed}
                              onClick={useAndSave}
                              classes={"btn btn-primary"} cssStyles={buttonCentering}/>
                <SubmitButton id="crmPaymentUseNotSaveSubmit" text={text("crm.text.payment.use.and.not.save")}
                              classes={"btn btn-primary"} cssStyles={buttonCentering} disabled={isSubmitting || !postalValidity || !crmCardNumberPassed} onClick={useAndNotSave}/>
              </>}
              {(mode === 'crmaddCard' && (modelName === 'crmPaymentTabAddNewCard'
                  || modelName === 'crmAddLRPNewCard' || modelName === 'crmSubscriptionAddNewCard')) &&
                <SubmitButton id="crmPaymentSaveSubmit" disabled={isSubmitting || !acceptPaymentTerms || !postalValidity || !crmCardNumberPassed} text={text("form.generic.button.save")}
                              classes={"btn btn-primary"} cssStyles={buttonCentering}/>}
              <SubmitButton id="cancelPaymentEdit" text={text("form.generic.button.cancel")} classes={"btn btn-primary"} cssStyles={buttonCentering}
                            onClick={cancelEditPaymentClicked}/>
            </div>
            <div className="text-left">
              <div style={{fontSize: '12px'}}>
                {text('doterra.recaptcha.sitemessage')} <a href="https://policies.google.com/privacy">{text('doterra.recaptcha.privacypolicymessage')}</a> {text(
                'doterra.recaptcha.andmessage')} <a href="https://policies.google.com/terms">{text('doterra.recaptcha.termservicemessage')}</a> {text(
                'doterra.recaptcha.applymessage')}
              </div>
            </div>
            <div className="text-center">
              <McAfeeLogo/>
            </div>
            <div id="recaptchacontainer" className="col-xs-12 col-lg-6">
              <ReCAPTCHA ref={recaptchaRef} size='invisible' badge='inline' className="pull-right" sitekey={recaptchaKey} hl={language}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CreditCardPanel = (props) => {
  const {
    markets,
    mode,
    cardMessage,
    onChange,
    onSubmit,
    modelName,
    paymentLineID,
    recaptchaKey,
    cartShippingAddress,
    csrfToken,
    setSubscriptionPayment,
    subscriptionCode
  } = props;

  const recaptchaRef = React.useRef();

  const {updateSelectedPaymentDate, hideModal, addPaymentHelp, editPaymentHelp} = usePaymentsMutator();
  const {showPaymentList} = usePaymentViewMutator();
  const {showGlobalModal, hideGlobalModal} = useModalsMutator();
  const {context, ccNonce} = usePaymentsState();

  const formSchema = {
    fields: {
      ccNumber: prop().required().creditCard().maxLength(25),
      cvv: prop().required(mode !== 'edit').numeric().minLength(mode === 'edit' ? 0 : 3).maxLength(mode === 'edit' ? 0 : 4),
      nameOnCard: prop().required().maxLength(35),
      expMonth: prop().required().numeric().maxLength(2),
      expYear: prop().required().numeric().minLength(4).maxLength(4),
      billingLine1: prop().required().maxLength(35),
      billingLine2: prop().maxLength(35),
      billingCity: prop().required().maxLength(35),
      billingState: prop().required().maxLength(35),
      billingZip: prop().required().maxLength(10),
      market: prop().required(),
      cardType: prop().required(),
      makeDefault: prop('boolean'),
      recaptchaResponse: prop()
    }
  };

  const handleChange = (formState) => {
    if(onChange) {
      onChange(formState);
    }
  };

  const handleSubmit = (values, setSubmitting) => {
    let encryptCCNumber = values.ccNumber;
    let crmTokenizationEnabled = $('#crmTokenizationEnabled').val() === 'true';

    if ((mode !== 'crmedit' && mode !== 'crmaddCard') || crmTokenizationEnabled) {
        encryptCCNumber = gv_strToken;
    }
    recaptchaRef.current.executeAsync().then(recaptchaResponse => {
      const data = {...values, context: context, ccNumber: encryptCCNumber,recaptchaResponse, nonce: ccNonce};

      if(onSubmit) { // It may be that an external component may want to handle the submit, so allow it if requested
        let submitResult = onSubmit(data);
        console.log(submitResult);
        if(!submitResult) {
          recaptchaRef.current.reset();
        }
        return;
      }

      const func = mode === "edit" || mode === "crmedit" ? editPaymentHelp : addPaymentHelp;
      func(data, subscriptionCode, csrfToken).then(result => {
        if(result) {
          if(mode === 'crmedit' || mode === 'crmaddCard' || mode === 'addCard' && modelName) {
            hideModal(modelName);
            if(data && mode === 'crmedit') {
              updateSelectedPaymentDate(data.expMonth, data.expYear, paymentLineID);
            }

            let isSubscriptionEnabled = $('#isSubscriptionEnabled').val() === 'true';
            if(isSubscriptionEnabled) {
              if(setSubscriptionPayment) {
                const cardCCNumber = values.ccNumber;
                let tmpCardNumber = '';
                if(cardCCNumber) {
                  if(mode === 'crmedit' || mode === 'crmaddCard') {
                    const lastSymbolPos = cardCCNumber.lastIndexOf("-");
                    if(lastSymbolPos) {
                      const crmCardNumberStr = cardCCNumber.substring(0, lastSymbolPos);
                      if(crmCardNumberStr && crmCardNumberStr.length > 4) {
                        tmpCardNumber = crmCardNumberStr.substring(crmCardNumberStr.length - 4);
                      }
                    }
                  }
                  else if(cardCCNumber.length > 4) {
                    tmpCardNumber = cardCCNumber.substring(cardCCNumber.length - 4);
                  }
                }

                const paymentData = {
                  method: 'card',
                  type: data.cardType,
                  number: tmpCardNumber
                };
                setSubscriptionPayment(paymentData);
              }
              let titleText = localText["text.lrp.titleSavedThisOrder"];
              let messageText = localText['text.lrp.savedThisOrder.orderreview'];
              if(context && (context === 'subscriptionSettings' || context === 'crm-subscription')) {
                messageText = localText['text.lrp.savedThisOrder'];
              }
              let closeText = localText["popup.close"];

              const modalName = 'add-credit-card';
              showGlobalModal(modalName, <SuccessModal text={messageText} title={titleText} closeText={closeText} close={() => hideGlobalModal(modalName)}
                                                       autoClose={5000}/>);
            }
          }
          else {
            showPaymentList();
          }
        }
        else {
          recaptchaRef.current.reset();
        }
        if(setSubmitting) {
          setSubmitting(false);
        }
      });
    });
  };

  return (
    <Form name='PaymentForm' method="POST" id="enrollmentPaymentInformationForm" schema={formSchema} initial={generateSchema(formSchema.fields)} onChange={handleChange}
          onSubmit={handleSubmit}>
      {(mode === 'crmedit' || mode === 'crmaddCard') &&
        <CrmCreditCardForm markets={markets} mode={mode} cardMessage={cardMessage} modelName={modelName} recaptchaKey={recaptchaKey} recaptchaRef={recaptchaRef}
                           csrfToken={csrfToken}/>}
      {(mode !== 'crmedit' && mode !== 'crmaddCard') &&
        <CreditCardForm markets={markets} mode={mode} cardMessage={cardMessage} recaptchaKey={recaptchaKey} recaptchaRef={recaptchaRef}
                        cartShippingAddress={cartShippingAddress}/>}
    </Form>
  );
};
